import { getAccessToken } from "../suite/auth";
import { candidateForgeApiBases } from "./generate";

type JobSnap = {
  id: string;
  status?: string;
  error?: string;
  code?: string;
  queuedAhead?: number;
};

export type ReworkProgress = {
  phase: "queued" | "running" | "fetch" | "waking";
  message: string;
};

function authHeaders(): Record<string, string> {
  const tok = getAccessToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
}

function jobMessage(job: JobSnap): string {
  const ahead = Number(job.queuedAhead || 0);
  if (job.code === "waking" || /waking/i.test(job.error || "")) {
    return "Waking ACE-Step box — first GPU boot can take several minutes.";
  }
  if (job.status === "queued" || ahead > 0) {
    return ahead > 0
      ? `Queued on ACE-Step GPU (${ahead} ahead)…`
      : "Queued on ACE-Step GPU…";
  }
  return "Reworking lyrics on ACE-Step…";
}

async function parseJob(res: Response): Promise<JobSnap | null> {
  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("json")) return null;
  try {
    return (await res.json()) as JobSnap;
  } catch {
    return null;
  }
}

async function startJob(
  base: string,
  audio: Blob,
  lyrics: string,
  caption: string,
  signal?: AbortSignal,
): Promise<{ job?: JobSnap; error?: string }> {
  const res = await fetch(`${base}/api/musicforge/rework/jobs`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": audio.type || "application/octet-stream",
      "X-Musicforge-Lyrics": encodeURIComponent(lyrics),
      "X-Musicforge-Caption": encodeURIComponent(caption),
      "X-Musicforge-Strength": "0.9",
      ...authHeaders(),
    },
    body: audio,
    signal,
  });
  const job = await parseJob(res);
  if (!job?.id) {
    const raw = job ? JSON.stringify(job) : await res.text().catch(() => "");
    return { error: raw.slice(0, 280) || `Rework failed (${res.status})` };
  }
  if (job.status === "error") {
    return { error: job.error || "Rework failed", job };
  }
  return { job };
}

async function pollJob(
  base: string,
  id: string,
  signal: AbortSignal | undefined,
  onProgress?: (p: ReworkProgress) => void,
): Promise<JobSnap> {
  const deadline = Date.now() + 12 * 60_000;
  while (Date.now() < deadline) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    const res = await fetch(`${base}/api/musicforge/rework/jobs/${encodeURIComponent(id)}`, {
      headers: { Accept: "application/json", ...authHeaders() },
      signal,
    });
    const job = await parseJob(res);
    if (!job) throw new Error(`Rework poll failed (${res.status})`);
    if (job.status === "ok" || job.status === "error") return job;
    onProgress?.({
      phase: job.code === "waking" ? "waking" : job.status === "queued" ? "queued" : "running",
      message: jobMessage(job),
    });
    await new Promise((r) => setTimeout(r, 2_000));
  }
  throw new Error("ACE-Step rework timed out after 12 minutes.");
}

async function fetchJobAudio(base: string, id: string, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(`${base}/api/musicforge/rework/jobs/${encodeURIComponent(id)}/audio`, {
    headers: { Accept: "audio/wav, application/json", ...authHeaders() },
    signal,
  });
  const ctype = res.headers.get("content-type") || "";
  if (!res.ok) {
    const raw = await res.text().catch(() => "");
    throw new Error(raw.slice(0, 280) || `Rework audio failed (${res.status})`);
  }
  if (ctype.includes("json")) {
    const j = (await res.json()) as { error?: string };
    throw new Error(j.error || "Reworked audio not ready");
  }
  return new Blob([await res.arrayBuffer()], { type: ctype || "audio/wav" });
}

export async function reworkLyrics(opts: {
  audio: Blob;
  lyrics: string;
  caption?: string;
  signal?: AbortSignal;
  onProgress?: (p: ReworkProgress) => void;
}): Promise<{ ok: true; blob: Blob; contentType: string } | { ok: false; error: string; waking?: boolean }> {
  const lyrics = opts.lyrics.trim();
  if (!lyrics) return { ok: false, error: "Add lyrics before Rework." };
  const bases = candidateForgeApiBases();
  let lastErr = "Rework failed";
  for (const base of bases) {
    try {
      const started = await startJob(base, opts.audio, lyrics, opts.caption || "", opts.signal);
      if (started.error) {
        lastErr = started.error;
        if (started.job?.code === "waking" || /waking/i.test(started.error)) {
          return { ok: false, waking: true, error: started.error };
        }
        if (/failed to fetch|networkerror|load failed|econnrefused/i.test(started.error)) continue;
        return { ok: false, error: lastErr };
      }
      const job = started.job!;
      opts.onProgress?.({
        phase: job.status === "queued" ? "queued" : "running",
        message: jobMessage(job),
      });
      const done = await pollJob(base, job.id, opts.signal, opts.onProgress);
      if (done.status === "error") {
        const waking = done.code === "waking" || /waking/i.test(done.error || "");
        return { ok: false, waking, error: done.error || "Rework failed" };
      }
      opts.onProgress?.({ phase: "fetch", message: "Fetching reworked take…" });
      const blob = await fetchJobAudio(base, job.id, opts.signal);
      return { ok: true, blob, contentType: blob.type || "audio/wav" };
    } catch (e) {
      if (e instanceof DOMException && e.name === "AbortError") throw e;
      lastErr = e instanceof Error ? e.message : String(e);
      if (/failed to fetch|networkerror|load failed|econnrefused/i.test(lastErr)) continue;
      return { ok: false, error: lastErr };
    }
  }
  return { ok: false, error: lastErr };
}
